/**
 * The account's API keys, read-only (task 980).
 *
 * The API Keys page (`src/pages/ApiKeysPage.tsx`, task 974) owns minting and
 * revoking; this hook exists so a surface that only needs to SAY something
 * about the keys — the Config tab's Access section, in API key mode — can read
 * the same list without carrying a mutation it must not offer.
 *
 * **Same key on purpose.** `ACCOUNT_API_KEYS_QUERY_KEY` is the tuple the page
 * already caches under (`API_KEYS_QUERY_KEY` there), so the two surfaces share
 * one cache entry and cannot report different counts; the page's mint/revoke
 * invalidations reach this read too. The query function therefore returns the
 * SAME `ApiKey[]` shape the page stores — a count cached under a shared key
 * would hand the page a number where it expects rows. The constant is repeated
 * rather than imported because a hook must not pull a page module (and its whole
 * import graph) in behind it; consolidate the two when the page is next edited.
 *
 * Failure is not flattened into "zero keys": a refused read (the
 * `innovate.be.api-keys.manage` gate answers 403) leaves the caller with
 * `undefined`, which the Access section renders as its countless sentence. The
 * read does not retry — one refusal is the answer, and this is a caption, not
 * the page whose job is the keys.
 */
import { useQuery } from '@tanstack/react-query';
import { apiKeysApi, type ApiKey } from '@/services/api';

/** Mirrors `API_KEYS_QUERY_KEY` in `src/pages/ApiKeysPage.tsx`. */
export const ACCOUNT_API_KEYS_QUERY_KEY = ['account', 'api-keys'] as const;

/** The page size the API Keys page reads with, kept identical so both agree. */
const API_KEYS_PAGE_SIZE = 100;

interface UseAccountApiKeysOptions {
  /** Only read when the surface actually shows something about the keys. */
  enabled?: boolean;
}

export function useAccountApiKeys({ enabled = true }: UseAccountApiKeysOptions = {}) {
  return useQuery<ApiKey[]>({
    queryKey: ACCOUNT_API_KEYS_QUERY_KEY,
    queryFn: () => apiKeysApi.list({ limit: API_KEYS_PAGE_SIZE }).then((res) => res.data.data),
    enabled,
    retry: false,
    staleTime: 30_000,
  });
}

/**
 * How many active keys the account has, or `null` while that is unknown — not
 * asked, not answered yet, or refused. The list route returns only active keys
 * (revocation is a soft delete), so its length IS the active count.
 */
export function useAccountApiKeyCount(options: UseAccountApiKeysOptions = {}): number | null {
  const { data, isSuccess } = useAccountApiKeys(options);
  return isSuccess ? (data?.length ?? 0) : null;
}
